import React, {ReactNode} from "react";
import {Script} from "gatsby";
import {TRACKING_CONFIG} from "./src/config";

interface WrapPageElementProps {
  element: ReactNode;
  props: any;
}

export const wrapPageElement = ({element, props}: WrapPageElementProps) => {
  const isRouteSearchPage =
    props?.location?.pathname?.startsWith("/route-search");
  const isAtlasPage = props?.location?.pathname?.startsWith("/atlas");

  // Ketch consent management script
  /*
    FMH-680: Security Issue (DOM-Based XSS):
    Previously, the "property" parameter in the query string could be manipulated to load
    scripts from unintended sources by including directory traversal sequences. This allowed
    attackers to inject malicious scripts. The updated code sanitizes the "property" value
    to mitigate this risk.

    FMH-808: By default, Ketch only fires tags on subsequent page loads after the user grants
    consent for a particular purpose. To ensure tags fire immediately upon consent without
    requiring a page reload, we use the on consent event.
  */
  const ketchConsentScript = (
    <Script
      key="ketch-consent"
      id="ketch-consent"
      strategy="post-hydrate"
      nonce="{{nonce}}"
      dangerouslySetInnerHTML={{
        __html: `
        !function(){
          window.semaphore = window.semaphore || [];
          window.ketch = function() {
            window.semaphore.push(arguments);
          };

          var safeProperty = "flexport_com";

          var j = document.querySelector('[nonce]');

          var n = document.createElement("script");
          n.type = "text/javascript";
          n.src = "https://global.ketchcdn.com/web/v3/config/flexport/" + safeProperty + "/boot.js";
          n.defer = n.async = true;
          n.setAttribute('nonce', j.nonce || j.getAttribute('nonce'));
          document.getElementsByTagName("head")[0].appendChild(n);
        }();

        window.ketch("on", "consent", (consent) => {
          const ketchUpdatedConsent = consent.purposes;

          window.ketch("getConfig", (ketchConfig) => {
            const purposeConfig = ketchConfig.purposes;

            var canonicalPurposeGoogleKeyMap = {
              behavioral_advertising: ["ad_storage", "ad_user_data"],
              analytics: ["analytics_storage"],
              essential_services: ["functionality_storage", "security_storage"],
              personalization: ["personalization_storage", "ad_personalization"],
            };

            let consentWithGooglePurposes = {};

            for (const purposeCode in ketchUpdatedConsent) {
              if (!purposeCode.includes(".")) {
                consentWithGooglePurposes[purposeCode] = ketchUpdatedConsent[purposeCode];
                for (const purpose of purposeConfig) {
                  if (purpose.code === purposeCode) {
                    const canonicalPurposes = purpose.canonicalPurposeCodes;
                    if (canonicalPurposes) {
                      for (const canonicalPurpose of canonicalPurposes) {
                        if (canonicalPurposeGoogleKeyMap[canonicalPurpose]) {
                          const gPurposes = canonicalPurposeGoogleKeyMap[canonicalPurpose];
                          for (const gp of gPurposes) {
                            if (consentWithGooglePurposes[gp]) {
                              consent[gp] = consentWithGooglePurposes[gp] || ketchUpdatedConsent[purposeCode];
                            } else {
                              consentWithGooglePurposes[gp] = ketchUpdatedConsent[purposeCode];
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }

            if (consent.purposes && window.gtmConsentListeners) {
              const consentUpdate = { purposes: consentWithGooglePurposes };
              window.gtmConsentListeners?.forEach((callback) => {
                  callback(consentUpdate);
              });
            }
          });
        });
        `,
      }}
    />
  );

  // Google reCAPTCHA setup script
  // How this code snippet works:
  // This logic overwrites the default behavior of 'grecaptcha.ready()' to
  // ensure that it can be safely called at any time. When 'grecaptcha.ready()'
  // is called before reCAPTCHA is loaded, the callback function that is passed
  // by 'grecaptcha.ready()' is enqueued for execution after reCAPTCHA is
  // loaded.
  const grecaptchaSetupScript = (
    <Script
      key="grecaptcha-setup"
      id="grecaptcha-setup"
      strategy="post-hydrate"
      nonce="{{nonce}}"
      dangerouslySetInnerHTML={{
        __html: `
        if (typeof grecaptcha === "undefined") {
          grecaptcha = {};
          grecaptcha.enterprise = {};
        }

        grecaptcha.enterprise.ready = function(cb) {
          if (typeof grecaptcha === "undefined") {
            // window.__grecaptcha_cfg is a global variable that stores reCAPTCHA's
            // configuration. By default, any functions listed in its 'fns' property
            // are automatically executed when reCAPTCHA loads.
            const c = "___grecaptcha_cfg";
            window[c] = window[c] || {};
            (window[c]["fns"] = window[c]["fns"]||[]).push(cb);
          } else {
            cb();
          }
        }
        `,
      }}
    />
  );

  // Google Tag Manager script
  const gtmScript = (
    <Script
      key="gtm-default"
      id="gtm-default"
      strategy="idle"
      nonce="{{nonce}}"
      dangerouslySetInnerHTML={{
        __html: TRACKING_CONFIG.GTM_DEFAULT.script,
      }}
    />
  );

  return (
    <>
      {isRouteSearchPage || isAtlasPage ? (
        <>{ketchConsentScript}</>
      ) : (
        <>
          {ketchConsentScript}
          {grecaptchaSetupScript}
          {gtmScript}
        </>
      )}
      {element}
    </>
  );
};
